import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  ScanLine, ShieldCheck, FileCheck2, QrCode, Sprout, Mountain,
  Globe2, BadgeCheck, ChevronRight, UserPlus, PackagePlus, Award, Users, HeartHandshake, Flame
} from "lucide-react";

// Same-origin images (served from /public/images) — no third-party CDN dependency
const IMAGES = {
  sugarcaneSunset:    "/images/sugarcane-sunset.webp",
  jaggeryPowder:      "/images/jaggery-powder.webp",
  processInfographic: "/images/jaggery-process.webp",
};

const HERO_BG_STYLE = {
  backgroundImage:
    "linear-gradient(115deg, rgba(20,32,18,0.78) 0%, rgba(43,26,10,0.55) 55%, rgba(140,83,32,0.35) 100%), url('/images/sugarcane-sunset.webp')",
  backgroundSize: "cover",
  backgroundPosition: "center",
};

export default function Landing() {
  return (
    <div>
      {/* HERO — desi jaggery cauldron backdrop */}
      <section className="hero-jaggery grain-overlay" style={HERO_BG_STYLE}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-[#FBEFD4] rise rise-1">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 px-3 py-1 backdrop-blur-sm">
              <Globe2 className="h-4 w-4" />
              <span className="text-xs uppercase tracking-[0.28em]">The Global Trust Mark for Indian Jaggery</span>
            </div>
            <h1 className="font-marketing font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6">
              When the world asks<br/>
              <em className="not-italic text-[hsl(34_85%_75%)]">"Is this jaggery real?"</em><br/>
              Gudlink answers.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#FBEFD4]/85 max-w-xl leading-relaxed">
              A global certification &amp; traceability network for India's natural jaggery — pilot-launched in Kolhapur,
              built for every jaggery-making region of the country. Every batch. Every seal. Every scan.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 rise rise-3">
              <Button asChild size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-6" data-testid="hero-cta-register">
                <Link to="/register">Become a certified FPO</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-6 bg-white/5 text-white border-white/40 hover:bg-white/15 hover:text-white" data-testid="hero-cta-verify">
                <Link to="/login">I'm a global buyer</Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-white/85 text-sm rise rise-4">
              <div className="flex items-center gap-2"><QrCode className="h-4 w-4"/>QR on every batch</div>
              <div className="flex items-center gap-2"><FileCheck2 className="h-4 w-4"/>3rd-party lab reports</div>
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4"/>SOP &amp; hygiene audited</div>
            </div>
          </div>

          {/* Trust card — no batch data, just imagery + Gudlink seal */}
          <div className="lg:col-span-5 rise rise-2" data-testid="hero-trust-card">
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full seal grid place-items-center text-center">
                <div>
                  <div className="text-[9px] tracking-[0.32em] uppercase">Gudlink</div>
                  <div className="font-marketing text-2xl leading-none mt-1">Verified</div>
                  <div className="text-[9px] tracking-[0.32em] uppercase mt-1">Est. 2026</div>
                </div>
              </div>
              <div className="rounded-3xl bg-[#FBEFD4]/95 border border-white/30 backdrop-blur-sm p-6 shadow-2xl">
                <img src={IMAGES.jaggeryPowder} alt="Natural Indian jaggery powder" className="w-full h-72 object-cover rounded-2xl"/>
                <div className="mt-5 flex items-center justify-between text-[color:hsl(var(--foreground))]">
                  <div>
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--secondary))]">The Gudlink Seal</div>
                    <div className="font-heading font-semibold text-lg mt-0.5">Traceable · Traditional · Trusted</div>
                  </div>
                  <BadgeCheck className="h-8 w-8 text-primary" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Global impact strip */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: "Global", k: "Verification network for Indian natural jaggery" },
            { v: "100%", k: "Batches carry QR + lab report + SOP on record" },
            { v: "< 1 hr", k: "FPO onboarding time — no paperwork chase" },
            { v: "0", k: "Middlemen between the buyer and the boiling cauldron" },
          ].map(x => (
            <div key={x.k} className="rounded-2xl bg-card border border-border p-6">
              <div className="font-marketing text-4xl text-primary">{x.v}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mt-3 leading-relaxed">{x.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story — Kolhapur / Sahyadri origin (jaggery + Indian farmers only) */}
      <section id="story" className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6 order-2 md:order-1">
            <div className="text-xs uppercase tracking-[0.28em] text-secondary">Our origin</div>
            <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-[1.05]">
              Starting in Kolhapur.<br/>Rooted across India.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed max-w-lg">
              Gudlink is being built for every jaggery-making region of India — Maharashtra, Uttar Pradesh, Karnataka,
              Tamil Nadu, Bihar and beyond. We're starting the pilot in Kolhapur because it's one of the country's
              highest-consumption jaggery belts and holds India's oldest desi wood-fired traditions. Once trust is
              proven here, we scale — one FPO, one region, one boiling cauldron at a time.
            </p>
            <ul className="mt-6 space-y-2 text-sm">
              <li className="flex gap-3"><Mountain className="h-4 w-4 text-primary mt-1"/>Pilot in the Sahyadri sugarcane belt (Kolhapur, Sangli, Satara)</li>
              <li className="flex gap-3"><Flame className="h-4 w-4 text-primary mt-1"/>Traditional wood-fired, chemical-free desi processing</li>
              <li className="flex gap-3"><Award className="h-4 w-4 text-primary mt-1"/>Scaling to every jaggery-producing state, GI-tag aligned</li>
            </ul>
          </div>
          <div className="md:col-span-6 order-1 md:order-2 grid grid-cols-2 gap-4">
            <img src={IMAGES.sugarcaneSunset} alt="Indian sugarcane field at sunset" className="h-[28rem] w-full object-cover rounded-2xl"/>
            <img src={IMAGES.jaggeryPowder} alt="Natural jaggery powder in wooden scoop" className="h-[28rem] w-full object-cover rounded-2xl"/>
          </div>
        </div>
      </section>

      {/* Traditional jaggery making process — user-provided infographic */}
      <section id="process" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="text-center max-w-3xl mx-auto">
          <div className="text-xs uppercase tracking-[0.28em] text-secondary">The desi standard</div>
          <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-tight">
            Our traditional jaggery making process.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            From cane to block — nine honest steps, done the old way. Every Gudlink-certified batch is checked against
            this same trail, so buyers know exactly what they're paying a premium for.
          </p>
        </div>
        <div className="mt-10 rounded-3xl overflow-hidden border border-border bg-card shadow-sm">
          <img src={IMAGES.processInfographic} alt="Nine-step traditional jaggery making process — harvesting, juice extraction, boiling, clarification, thickening, final check, moulding, cooling, packaging" className="w-full h-auto block"/>
        </div>
      </section>

      {/* EASY FPO REGISTRATION — 3-step visual guide */}
      <section id="join-fpo" className="paper border-y border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-xs uppercase tracking-[0.28em] text-secondary">For FPOs &amp; Jaggery Farmers</div>
            <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-tight">
              Get certified in 3 easy steps.<br/>
              <span className="text-primary">No paperwork chase.</span>
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              Whether you're a single processor in Kolhapur or a 200-farmer FPO in Uttar Pradesh — Gudlink onboards you in under an hour,
              from your phone.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01", icon: UserPlus, tag: "Sign up",
                title: "Register your FPO",
                copy: "Name, location, contact. That's it. No printouts, no field officer visit — you're in.",
                cta: { label: "Start registration", to: "/register" }
              },
              {
                n: "02", icon: PackagePlus, tag: "Add a batch",
                title: "Log your first batch",
                copy: "Batch code, quantity, harvest & processing dates, product type. Attach lab report + a simple SOP checklist.",
                cta: { label: "Start now", to: "/register" }
              },
              {
                n: "03", icon: QrCode, tag: "Get QR",
                title: "Print the trust seal",
                copy: "We generate a unique QR for each batch. Print on packaging — buyers anywhere in the world can verify with one scan.",
                cta: { label: "Sign in to view", to: "/login" }
              },
            ].map((s, i) => (
              <div key={s.n} className="relative rounded-3xl bg-card border border-border p-7 flex flex-col rise" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="font-marketing text-6xl text-primary/10 absolute top-2 right-4">{s.n}</div>
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-[10px] uppercase tracking-[0.25em] px-3 py-1 w-fit border border-primary/20">
                  <s.icon className="h-3.5 w-3.5"/>{s.tag}
                </div>
                <div className="font-heading text-xl font-semibold mt-4">{s.title}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.copy}</p>
                <div className="mt-auto pt-4">
                  <Link to={s.cta.to} className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:gap-2 transition-all">
                    {s.cta.label} <ChevronRight className="h-4 w-4"/>
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { icon: HeartHandshake, t: "Zero listing fee during pilot" },
              { icon: Users, t: "Marathi & Hindi support (coming soon)" },
              { icon: FileCheck2, t: "Upload lab reports as PDF or photo" },
              { icon: BadgeCheck, t: "Get verified in 24–48 hours" },
            ].map((x, i) => (
              <div key={i} className="rounded-2xl bg-card border border-border p-4 flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-primary/10 text-primary grid place-items-center"><x.icon className="h-4 w-4"/></div>
                <div className="text-sm font-medium">{x.t}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="rounded-full px-8 bg-primary hover:bg-primary/90" data-testid="join-fpo-btn">
              <Link to="/register">Register my FPO — it's free</Link>
            </Button>
            <span className="text-xs text-muted-foreground">Takes under 5 minutes · No credit card</span>
          </div>
        </div>
      </section>

      {/* For whom — FPO + Buyer only (admin removed) */}
      <section id="users" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-8">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-[0.28em] text-secondary">Who Gudlink serves</div>
          <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-[1.05]">
            One trust mark.<br/>Two sides of the trade.
          </h2>
          <p className="mt-5 text-muted-foreground leading-relaxed max-w-md">
            Producers earn a premium. Buyers stop guessing. And nobody has to trust a WhatsApp forward ever again.
          </p>
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
          {[
            { title: "FPOs & Processors", copy: "Log batches, upload proofs, share a QR. Premium orders — without the WhatsApp back-and-forth.", tag: "Producer" },
            { title: "Global B2B Buyers", copy: "Scan before you sign. Verify origin, purity, hygiene — in the language of your compliance team.", tag: "Buyer" },
          ].map((c) => (
            <div key={c.title} className="rounded-2xl bg-card border border-border p-6 flex flex-col hover:shadow-sm transition-shadow">
              <div className="text-[10px] uppercase tracking-[0.25em] text-secondary">{c.tag}</div>
              <div className="font-heading font-semibold text-lg mt-1">{c.title}</div>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">{c.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Buyer trust callout — cauldron backdrop (no tea terraces) */}
      <section className="hero-jaggery grain-overlay" style={HERO_BG_STYLE}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-white">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-white/80">For global buyers</div>
              <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-tight">
                Buy Indian jaggery the way global buyers buy everything else — with proof.
              </h2>
              <p className="mt-5 text-white/85 max-w-lg leading-relaxed">
                Every Gudlink batch carries its origin, lab test, hygiene SOP and processor identity — accessible in one scan.
                Meet HACCP / FSSAI / GI expectations without a compliance dossier the size of a novel.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button asChild size="lg" className="bg-white text-[hsl(var(--foreground))] hover:bg-white/90 rounded-full px-6" data-testid="buyer-cta-btn">
                  <Link to="/login"><ScanLine className="h-4 w-4 mr-2"/>Verify a batch now</Link>
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { k: "Batches on record", v: "Real-time" },
                { k: "Time to verify", v: "Under 30 sec" },
                { k: "Documents per batch", v: "Lab · SOP · QR" },
                { k: "Public verify page", v: "No login needed" },
              ].map(x => (
                <div key={x.k} className="rounded-2xl bg-white/10 border border-white/20 p-5 backdrop-blur-sm">
                  <div className="text-2xl font-heading font-semibold">{x.v}</div>
                  <div className="text-xs uppercase tracking-[0.22em] text-white/85 mt-1">{x.k}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-center">
        <div className="text-xs uppercase tracking-[0.28em] text-secondary">Join the network</div>
        <h2 className="font-marketing text-4xl sm:text-5xl mt-3 leading-tight max-w-3xl mx-auto">
          The world is looking for real jaggery.<br/>Let it find yours.
        </h2>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg" className="rounded-full px-6 bg-primary hover:bg-primary/90" data-testid="cta-join-btn">
            <Link to="/register">Create your account</Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="rounded-full px-6" data-testid="cta-scan-btn">
            <Link to="/login"><ScanLine className="h-4 w-4 mr-2"/>I'm a buyer, let me in</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
